import axios, { type AxiosInstance, type AxiosResponse, AxiosError } from "axios"
import { SchultenMedia } from "@/services/DataService"
import { getStoredLanguage, i18n } from '@/i18n'
import { DISTRIBUTION, DISTRIBUTION_HEADER } from '@/config/distribution'

/**
 * The result of a read.
 *
 * `get()` used to return the payload on success and `null` on *any* failure,
 * which made "the server said no", "we are offline" and "here is your data,
 * and it happens to be null" indistinguishable. Every caller then reached for
 * `response.data` and crashed with "Cannot read properties of null (reading
 * 'data')" the moment a request failed.
 *
 * Making the failure part of the type means a caller cannot reach the payload
 * without saying what happens when there isn't one - the compiler asks.
 */
export type ApiResult<T = any> =
    | { ok: true; data: T }
    | { ok: false; networkError: true }
    | { ok: false; status: number }

/** Narrowing helper for the two failure shapes. */
export const apiStatus = (result: ApiResult): number | null =>
    result.ok || !("status" in result) ? null : result.status

/**
 * What a failed login, forgot-password, reset-code or impersonation call says to the
 * user. Only a text the backend wrote for the user (a 4xx with a string message, in
 * `error.message` or `message`) is passed on. Empty otherwise, so the screen shows its
 * own translated text instead of axios' "timeout of 10000ms exceeded", an HTML page or
 * Laravel's "Server Error". A 429 is Laravel's untranslated "Too Many Attempts.".
 */
const unauthorizedErrorMessage = (error: AxiosError<any>): string | undefined => {
    const status = error.response?.status
    if (status === 429) return i18n.global.t("Too many attempts. Please try again in a few minutes.")
    if (!status || status >= 500) return undefined

    const data = error.response?.data
    const message = data?.error?.message ?? data?.message
    return typeof message === "string" && message.trim() !== "" ? message : undefined
}

// A file upload over a mobile connection regularly needs more than the 10 s every
// other call gets: a 0.6 MB photo took up to 12 s (COACHIO-1859).
const UPLOAD_TIMEOUT = 30 * 1000

class ApiService {
    private axiosInstance: AxiosInstance

    public constructor() {
        this.axiosInstance = axios.create({
            baseURL: `${SchultenMedia.API_URL}/${SchultenMedia.API_VERSION}`,
            timeout: 10 * 1000, // 10 seconds
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "Accept-Language": "nl",
                "X-App-Locale": "nl",
                [DISTRIBUTION_HEADER]: DISTRIBUTION.id,
            },
        })

        // Request interceptor to add auth token
        this.axiosInstance.interceptors.request.use(
            (config) => {
                if (SchultenMedia.Bearer) {
                    config.headers.Authorization = SchultenMedia.Bearer
                }

                if (config.data instanceof FormData && (config.timeout ?? 0) < UPLOAD_TIMEOUT) {
                    config.timeout = UPLOAD_TIMEOUT
                }

                return config
            },
            (error) => {
                return Promise.reject(error)
            },
        )

        // Response interceptor to handle errors globally
        this.axiosInstance.interceptors.response.use(
            (response: AxiosResponse) => {
                return response
            },
            (error: AxiosError) => {
                console.error("API Error:", error)

                // Handle specific error cases
                if (error.response?.status === 401) {
                    // Token expired or invalid
                    SchultenMedia.Bearer = ""
                    SchultenMedia.Authenticated = false
                    // Redirect to login if needed
                }

                return Promise.reject(error)
            },
        )

        // Initialize locale asynchronously
        this.initializeLocale()
    }

    /**
     * Initialize locale from storage
     */
    private async initializeLocale(): Promise<void> {
        this.axiosInstance.interceptors.request.use(
            async (config) => {
                try {
                    const storedLocale = await getStoredLanguage();

                    config.headers['X-App-Locale'] = storedLocale;
                    config.headers['Accept-Language'] = storedLocale;

                    console.log('Request with locale:', storedLocale);
                } catch (error) {
                    console.warn('Error getting locale for request:', error);
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }

    async get<T = any>(
        endpoint: string,
        config?: { params?: object, signal?: AbortSignal },
    ): Promise<ApiResult<T>> {
        try {
            const response = await this.axiosInstance.get(endpoint, config)
            return { ok: true, data: response.data }
        } catch (error) {
            console.error("GET Error:", error)
            return ApiService.failure(error)
        }
    }

    /**
     * Turn a thrown value into a failure result. A thrown non-Axios value
     * (a TypeError in an interceptor, a bare string) is treated as "no
     * response", which is the honest reading: we never heard back.
     */
    private static failure(error: unknown): { ok: false; networkError: true } | { ok: false; status: number } {
        if (error instanceof AxiosError && error.response) {
            return { ok: false, status: error.response.status }
        }
        return { ok: false, networkError: true }
    }

    /**
     * GET that preserves WHY it failed, so auth can tell "offline" apart from
     * "token expired". Unlike `get()` (which returns null for everything), this
     * reports the HTTP status when the server responded, or a network-error flag
     * when there was no response at all (offline / timeout / unreachable).
     */
    async verifyToken(
        endpoint: string,
    ): Promise<
        | { ok: true; data: any }
        | { ok: false; networkError: true }
        | { ok: false; status: number }
    > {
        try {
            const response = await this.axiosInstance.get(endpoint)
            return { ok: true, data: response.data }
        } catch (error) {
            if (error instanceof AxiosError && error.response) {
                // Server responded with an error status (401, 500, ...).
                return { ok: false, status: error.response.status }
            }
            // No response: offline, DNS failure, timeout, server unreachable.
            return { ok: false, networkError: true }
        }
    }

    async post(endpoint: string, data: any, config: object = {}) {
        try {
            const response = await this.axiosInstance.post(endpoint, data, config)
            return response.data
        } catch (error) {
            console.error("POST Error:", error)
            if (error instanceof AxiosError) {
                return {
                    error: true,
                    message: error.response?.data?.message || error.message,
                    status: error.response?.status,
                    response: error.response,
                }
            }
            return {
                error: true,
                message: "An unexpected error occurred",
                originalError: error,
            }
        }
    }

    async postUnauthorized(endpoint: string, data: any) {
        try {
            const storedLocale = await getStoredLanguage();

            const response = await axios.post(`${SchultenMedia.API_URL}/${SchultenMedia.API_VERSION}${endpoint}`, data, {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "Accept-Language": storedLocale,
                    "X-App-Locale": storedLocale,
                    [DISTRIBUTION_HEADER]: DISTRIBUTION.id,
                },
                timeout: 10 * 1000, // 10 seconds
            })
            return response.data
        } catch (error) {
            console.error("POST Unauthorized Error:", error)
            if (error instanceof AxiosError) {
                return {
                    error: true,
                    message: unauthorizedErrorMessage(error),
                    status: error.response?.status,
                    response: error.response,
                }
            }
            return {
                error: true,
                message: "An unexpected error occurred",
                originalError: error,
            }
        }
    }

    async put(endpoint: string, data?: any) {
        try {
            const response = await this.axiosInstance.put(endpoint, data)
            return response.data
        } catch (error) {
            console.error("PUT Error:", error)
            if (error instanceof AxiosError) {
                return {
                    error: true,
                    message: error.response?.data?.message || error.message,
                    status: error.response?.status,
                    response: error.response,
                }
            }
            // A non-Axios throw is still a failure, not "no result": returning
            // null here put callers back in the same read-a-property-off-null
            // trap that `get()` had.
            return {
                error: true,
                message: error instanceof Error ? error.message : "An unexpected error occurred",
                originalError: error,
            }
        }
    }

    async delete(endpoint: string, data?: any) {
        try {
            const response = await this.axiosInstance.delete(endpoint, {
                data,
            })
            return response.data
        } catch (error) {
            console.error("DELETE Error:", error)
            if (error instanceof AxiosError) {
                return {
                    error: true,
                    message: error.response?.data?.message || error.message,
                    status: error.response?.status,
                    ...error.response?.data,
                }
            }
            // A non-Axios throw is still a failure, not "no result": returning
            // null here put callers back in the same read-a-property-off-null
            // trap that `get()` had.
            return {
                error: true,
                message: error instanceof Error ? error.message : "An unexpected error occurred",
                originalError: error,
            }
        }
    }

    async patch(endpoint: string, data?: any) {
        try {
            const response = await this.axiosInstance.patch(endpoint, data)
            return response.data
        } catch (error) {
            console.error("PATCH Error:", error)
            if (error instanceof AxiosError) {
                return {
                    error: true,
                    message: error.response?.data?.message || error.message,
                    status: error.response?.status,
                }
            }
            // A non-Axios throw is still a failure, not "no result": returning
            // null here put callers back in the same read-a-property-off-null
            // trap that `get()` had.
            return {
                error: true,
                message: error instanceof Error ? error.message : "An unexpected error occurred",
                originalError: error,
            }
        }
    }

    async downloadFile(endpoint: string, params?: object): Promise<Blob> {
        try {
            const response = await this.axiosInstance.get(endpoint, {
                params,
                responseType: 'blob',
                headers: {
                    'Accept': 'application/pdf,application/octet-stream',
                }
            })
            return response.data
        } catch (error) {
            console.error("Download Error:", error)
            throw error
        }
    }
}

export const Api = new ApiService()
